/**
 * Acceso a Drive del usuario. Prefiere GIS si hay client ID;
 * si no, popup de Google en una app Firebase aparte (no toca el login actual).
 */
import { initializeApp, getApp, getApps } from 'firebase/app';
import { getAuth, GoogleAuthProvider, signInWithPopup, signOut } from 'firebase/auth';
import { firebaseConfig } from '../../../firebaseConfig';

const FOLDER_MIME = 'application/vnd.google-apps.folder';
const SHEET_MIME = 'application/vnd.google-apps.spreadsheet';
const DRIVE_SCOPE = 'https://www.googleapis.com/auth/drive.readonly';
const SHEETS_SCOPE = 'https://www.googleapis.com/auth/spreadsheets.readonly';
const GOOGLE_SCOPES = `${DRIVE_SCOPE} ${SHEETS_SCOPE}`;
const DRIVE_APP_NAME = 'drive-oauth';

let gisPromise = null;
let accessToken = '';
let tokenExpiry = 0;
let tokenClient = null;
let driveAuth = null;

function driveAuthInstance() {
    if (driveAuth) return driveAuth;
    const app = getApps().some((item) => item.name === DRIVE_APP_NAME)
        ? getApp(DRIVE_APP_NAME)
        : initializeApp(firebaseConfig, DRIVE_APP_NAME);
    driveAuth = getAuth(app);
    return driveAuth;
}

function firebaseDriveError(err) {
    const code = err?.code || '';
    if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') {
        return 'Cerraste la ventana de Google.';
    }
    if (code === 'auth/operation-not-allowed') {
        return 'Activa el proveedor Google en Firebase Authentication (Authentication → Sign-in method).';
    }
    if (code === 'auth/unauthorized-domain') {
        return 'Este dominio no está autorizado en Firebase Authentication.';
    }
    return err?.message || 'No se pudo conectar con Google Drive.';
}

async function getTokenViaFirebasePopup() {
    const provider = new GoogleAuthProvider();
    provider.addScope(DRIVE_SCOPE);
    provider.addScope(SHEETS_SCOPE);
    provider.setCustomParameters({ prompt: 'select_account' });
    const result = await signInWithPopup(driveAuthInstance(), provider);
    const cred = GoogleAuthProvider.credentialFromResult(result);
    const token = cred?.accessToken;
    try {
        await signOut(driveAuthInstance());
    } catch (_err) {
        /* la sesión de Drive es auxiliar */
    }
    if (!token) {
        throw new Error('Google no concedió acceso a Drive. Vuelve a conectar y acepta el permiso.');
    }
    return token;
}

function loadScript(src) {
    return new Promise((resolve, reject) => {
        const existing = document.querySelector(`script[src="${src}"]`);
        if (existing && window.google?.accounts?.oauth2) {
            resolve();
            return;
        }
        const script = document.createElement('script');
        script.src = src;
        script.async = true;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error('No se pudo cargar Google Identity.'));
        document.head.appendChild(script);
    });
}

export function loadGoogleIdentity() {
    if (!gisPromise) {
        gisPromise = loadScript('https://accounts.google.com/gsi/client');
    }
    return gisPromise;
}

export function mapDriveFile(item) {
    const mime = item.mimeType || '';
    const isFolder = mime === FOLDER_MIME;
    const owners = item.owners || [];
    const owner = owners[0] || {};
    return {
        id: item.id,
        name: item.name || 'Sin nombre',
        mimeType: mime,
        kind: isFolder ? 'folder' : 'sheet',
        modifiedTime: item.modifiedTime,
        owner: owner.displayName || owner.emailAddress || '',
        url: item.webViewLink || (isFolder ? '' : `https://docs.google.com/spreadsheets/d/${item.id}`),
    };
}

async function getTokenViaGis(clientId, force = false) {
    await loadGoogleIdentity();
    if (!window.google?.accounts?.oauth2) {
        throw new Error('Google Identity no está disponible.');
    }
    return new Promise((resolve, reject) => {
        tokenClient = window.google.accounts.oauth2.initTokenClient({
            client_id: clientId,
            scope: GOOGLE_SCOPES,
            callback: (resp) => {
                if (resp.error) {
                    reject(new Error(resp.error === 'access_denied'
                        ? 'No se concedió acceso a Google Drive.'
                        : `Google OAuth: ${resp.error}`));
                    return;
                }
                resolve(resp.access_token);
            },
        });
        tokenClient.requestAccessToken({ prompt: force || !accessToken ? 'consent' : '' });
    });
}

export async function getDriveAccessToken(clientId, { force = false } = {}) {
    if (!force && accessToken && Date.now() < tokenExpiry - 60 * 1000) {
        return accessToken;
    }
    if (force) {
        accessToken = '';
        tokenExpiry = 0;
    }
    const id = String(clientId || '').trim();
    let token;
    try {
        token = id ? await getTokenViaGis(id, force) : await getTokenViaFirebasePopup();
    } catch (err) {
        if (id) {
            try {
                token = await getTokenViaFirebasePopup();
            } catch (fallbackErr) {
                throw new Error(firebaseDriveError(fallbackErr));
            }
        } else {
            throw new Error(firebaseDriveError(err));
        }
    }
    accessToken = token;
    tokenExpiry = Date.now() + 55 * 60 * 1000;
    return accessToken;
}

export function peekDriveAccessToken() {
    if (accessToken && Date.now() < tokenExpiry - 60 * 1000) return accessToken;
    return '';
}

export function clearDriveToken() {
    accessToken = '';
    tokenExpiry = 0;
}

export async function driveListFiles(clientId, { query, folderId, place, pageToken, pageSize = 50, driveId }) {
    const token = await getDriveAccessToken(clientId);
    const headers = { Authorization: `Bearer ${token}` };
    const cleaned = String(query || '').trim();
    const where = String(place || 'my_drive');
    const folder = String(folderId || 'root').trim() || 'root';
    const sharedId = String(driveId || '').trim();

    if (!cleaned && where === 'shared_drives' && (folder === 'root' || folder === 'shared_root')) {
        const params = new URLSearchParams();
        params.set('pageSize', String(Math.max(1, Math.min(80, pageSize))));
        params.set('fields', 'nextPageToken,drives(id,name)');
        const response = await fetch(`https://www.googleapis.com/drive/v3/drives?${params.toString()}`, { headers });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
            throw new Error(payload.error?.message || 'No se pudieron listar las unidades compartidas.');
        }
        const files = (payload.drives || []).filter((item) => item.id).map((item) => ({
            id: item.id,
            name: item.name || 'Unidad compartida',
            mimeType: 'application/vnd.google-apps.folder',
            kind: 'shared_drive',
            driveId: item.id,
            modifiedTime: '',
            owner: '',
            url: '',
        }));
        files.sort((a, b) => String(a.name).localeCompare(String(b.name), 'es'));
        return {
            files,
            next_page_token: payload.nextPageToken || '',
            impersonated: true,
            listing_as: 'google-oauth',
        };
    }

    const params = new URLSearchParams();
    params.set('pageSize', String(Math.max(1, Math.min(80, pageSize))));
    params.set('fields', 'nextPageToken,files(id,name,mimeType,modifiedTime,owners(displayName,emailAddress),webViewLink)');
    params.set('supportsAllDrives', 'true');
    params.set('includeItemsFromAllDrives', 'true');
    if (cleaned) {
        const escaped = cleaned.replace(/\\/g, '\\\\').replace(/'/g, "\\'");
        params.set('q', `trashed=false and (mimeType='${SHEET_MIME}' or mimeType='${FOLDER_MIME}') and name contains '${escaped}'`);
        params.set('orderBy', 'modifiedTime desc');
        params.set('corpora', 'allDrives');
    } else if (where === 'shared_with_me') {
        params.set('q', `sharedWithMe=true and trashed=false and (mimeType='${SHEET_MIME}' or mimeType='${FOLDER_MIME}')`);
        params.set('orderBy', 'modifiedTime desc');
    } else if (where === 'shared_drives' || sharedId) {
        const parent = folder === 'shared_root' ? sharedId : folder;
        params.set('q', `'${parent}' in parents and trashed=false and (mimeType='${SHEET_MIME}' or mimeType='${FOLDER_MIME}')`);
        params.set('orderBy', 'folder,name');
        params.set('corpora', 'drive');
        params.set('driveId', sharedId || parent);
    } else {
        params.set('q', `'${folder}' in parents and trashed=false and (mimeType='${SHEET_MIME}' or mimeType='${FOLDER_MIME}')`);
        params.set('orderBy', 'folder,name');
        params.set('corpora', 'user');
    }
    if (pageToken) params.set('pageToken', pageToken);
    const response = await fetch(`https://www.googleapis.com/drive/v3/files?${params.toString()}`, { headers });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
        const message = payload.error?.message || 'No se pudo listar tu Google Drive.';
        throw new Error(message);
    }
    const files = (payload.files || []).filter((item) => item.id).map((item) => {
        const mapped = mapDriveFile(item);
        if (sharedId) mapped.driveId = sharedId;
        return mapped;
    });
    files.sort((a, b) => {
        if (a.kind !== b.kind) return a.kind === 'folder' || a.kind === 'shared_drive' ? -1 : 1;
        return String(a.name).localeCompare(String(b.name), 'es');
    });
    return {
        files,
        next_page_token: payload.nextPageToken || '',
        impersonated: true,
        listing_as: 'google-oauth',
    };
}
